import { Moment } from 'moment';
import { Inject, Injectable } from '@angular/core';

import { getValueFromNamespace, setValueInNamespace } from '@lib/store2';
import { moment } from '@lib/moment';
import { Session } from './session';
import { IFuelScoreFeedback } from '@app/dashboard/fuel-insights-score/fuel-insights.interface';
import { IContact, IStoredUserLocation } from '@app/load-board/load-board.service';
import { DRIVERS_ACCORDION_STATES } from '@app/load-board/load-board.utils';

const USER_NS = 'USER_';
const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';

@Injectable({ providedIn: 'root' })
export class LocalStorage {
  constructor(@Inject(Session) private session) {}

  public getUserSettings(userId: string = this.session.getUserId()): any {
    const settings = getValueFromNamespace('settings', USER_NS + userId);
    return settings ? settings : {};
  }

  public setUserSettings(data: any, userId: string = this.session.getUserId()): void {
    setValueInNamespace('settings', data, USER_NS + userId);
  }

  public getLoadBoardVisitedStatus(userId: string = this.session.getUserId()): boolean {
    const date: string | boolean = getValueFromNamespace('loadBoardVisited', USER_NS + userId);
    const prevSunday: Moment = moment().startOf('week').add(1, 'minute');
    if (typeof date !== 'boolean') return moment(date, DATE_FORMAT).isAfter(prevSunday);
    return false;
  }

  public setLastBidParams(data: IContact, userId: string = this.session.getUserId()) {
    setValueInNamespace('loadBoardBidParams', data, USER_NS + userId);
  }

  public getLastBidParams(userId: string = this.session.getUserId()): IContact {
    const params = getValueFromNamespace('loadBoardBidParams', USER_NS + userId);
    return params ? params : {};
  }

  public setLastBookParams(data: IContact, userId: string = this.session.getUserId()) {
    setValueInNamespace('loadBoardBookParams', data, USER_NS + userId);
  }

  public getLastBookParams(userId: string = this.session.getUserId()): IContact {
    const params = getValueFromNamespace('loadBoardBookParams', USER_NS + userId);
    return params ? params : {};
  }

  public setDriversAccordionState(status: DRIVERS_ACCORDION_STATES, userId: string = this.session.getUserId()): void {
    setValueInNamespace('loadBoardDriversAccordionState', status, userId);
  }

  public getDriversAccordionState(userId: string = this.session.getUserId()): DRIVERS_ACCORDION_STATES {
    return getValueFromNamespace('loadBoardDriversAccordionState', userId);
  }

  public setUserLocation(locationData: IStoredUserLocation, userId: string = this.session.getUserId()): void {
    setValueInNamespace('userLocationData', locationData, USER_NS + userId);
  }

  public getUserLocation(userId: string = this.session.getUserId()): IStoredUserLocation {
    const locationData: IStoredUserLocation = getValueFromNamespace('userLocationData', USER_NS + userId);
    if (locationData && moment(locationData.date, DATE_FORMAT).isSame(new Date(), 'month')) return locationData;
    return null;
  }

  public setFueScoreFeedbackStatus(feedbackData: IFuelScoreFeedback): void {
    setValueInNamespace('userFuelScoreFeedback', feedbackData, USER_NS + this.session.getUserId());
  }

  public getFueScoreFeedbackStatus(): IFuelScoreFeedback {
    return getValueFromNamespace('userFuelScoreFeedback', USER_NS + this.session.getUserId());
  }


  // Fleet Card

  public setFleetCardRewardSectionHideDate(date: string | null): void {
    setValueInNamespace('fleetCardRewardSectionHideDate', date, USER_NS + this.session.getUserId());
  }

  public getFleetCardRewardSectionHideDate(): string {
    return getValueFromNamespace('fleetCardRewardSectionHideDate', USER_NS + this.session.getUserId());
  }

  public setBannerCloseDate(currentDate: string): void {
    setValueInNamespace('browserBannerClosed', currentDate,'lastDateBannerClosed');
  }

  public getBannerCloseDate(): string {
    return getValueFromNamespace('browserBannerClosed','lastDateBannerClosed');
  }

  public setUpsellStickyPromoBannerNextDisplayDate(date: string): void {
    setValueInNamespace('upsellStickyPromoBannerNextDisplayDate', date, USER_NS + this.session.getUserId());
  }

  public getUpsellStickyPromoBannerNextDisplayDate(): string {
    return getValueFromNamespace('upsellStickyPromoBannerNextDisplayDate', USER_NS + this.session.getUserId());
  }

  public setUpsellStickyPromoBannerDismissCount(count: number): void {
    setValueInNamespace('upsellStickyPromoBannerDismissCount', count, USER_NS + this.session.getUserId());
  }

  public getUpsellStickyPromoBannerDismissCount(): number {
    return getValueFromNamespace('upsellStickyPromoBannerDismissCount', USER_NS + this.session.getUserId());
  }

  public setFleetCardApplicationFirstVisitedDate(date: string | null): void {
    setValueInNamespace('fleetCardApplicationFirstVisitedDate', date, USER_NS + this.session.getUserId());
  }

  public getFleetCardApplicationFirstVisitedDate(): string {
    return getValueFromNamespace('fleetCardApplicationFirstVisitedDate', USER_NS + this.session.getUserId());
  }

  public setFleetCardApplicationNudgeApplyNowDismissCount(count: number): void {
    setValueInNamespace('fleetCardApplicationNudgeApplyNowDismissCount', count, USER_NS + this.session.getUserId());
  }

  public getFleetCardApplicationNudgeApplyNowDismissCount(): number {
    return getValueFromNamespace('fleetCardApplicationNudgeApplyNowDismissCount', USER_NS + this.session.getUserId());
  }

  public setFleetCardApplicationNudgeFinishAppDismissCount(count: number): void {
    setValueInNamespace('fleetCardApplicationNudgeFinishAppDismissCount', count, USER_NS + this.session.getUserId());
  }

  public getFleetCardApplicationNudgeFinishAppDismissCount(): number {
    return getValueFromNamespace('fleetCardApplicationNudgeFinishAppDismissCount', USER_NS + this.session.getUserId());
  }

  public setFleetCardApplicationNudgeLinkBankAccDismissCount(count: number): void {
    setValueInNamespace('fleetCardApplicationNudgeLinkBankAccDismissCount', count, USER_NS + this.session.getUserId());
  }

  public getFleetCardApplicationNudgeLinkBankAccDismissCount(): number {
    return getValueFromNamespace('fleetCardApplicationNudgeLinkBankAccDismissCount', USER_NS + this.session.getUserId());
  }

  public getFleetCardApplicationFleetBundle3DDismissCount(): number {
    return getValueFromNamespace('fleetCardApplicationFleetBundle3DDismissCount', USER_NS + this.session.getUserId());
  }

  public setFleetCardApplicationFleetBundle3DDismissCount(count: number): void {
    setValueInNamespace('fleetCardApplicationFleetBundle3DDismissCount', count, USER_NS + this.session.getUserId());
  }

  public getFleetCardApplicationFleetBundle15DDismissCount(): number {
    return getValueFromNamespace('fleetCardApplicationFleetBundle15DDismissCount', USER_NS + this.session.getUserId());
  }

  public setFleetCardApplicationFleetBundle15DDismissCount(count: number): void {
    setValueInNamespace('fleetCardApplicationFleetBundle15DDismissCount', count, USER_NS + this.session.getUserId());
  }

  public getFleetCardApplicationFleetBundle45DDismissCount(): number {
    return getValueFromNamespace('fleetCardApplicationFleetBundle45DDismissCount', USER_NS + this.session.getUserId());
  }

  public setFleetCardApplicationFleetBundle45DDismissCount(count: number): void {
    setValueInNamespace('fleetCardApplicationFleetBundle45DDismissCount', count, USER_NS + this.session.getUserId());
  }

  public getFleetCardNotificationDot(): boolean {
    return getValueFromNamespace('fleetCardNotificationDot', USER_NS + this.session.getUserId());
  }

  public setFleetCardNotificationDot(value: boolean): void {
    setValueInNamespace('fleetCardNotificationDot', value, USER_NS + this.session.getUserId());
  }

  public setFleetCardApplicationNudgeNextDisplayDate(date: string): void {
    setValueInNamespace('fleetCardApplicationNudgeNextDisplayDate', date, USER_NS + this.session.getUserId());
  }

  public getFleetCardApplicationNudgeNextDisplayDate(): string {
    return getValueFromNamespace('fleetCardApplicationNudgeNextDisplayDate', USER_NS + this.session.getUserId());
  }

  public setFleetCardLinkBankAccountClicked(): void {
    setValueInNamespace('fleetCardLinkBankAccountClicked', true, USER_NS + this.session.getUserId());
  }

  public getFleetCardLinkBankAccountClicked(): boolean {
    return getValueFromNamespace('fleetCardLinkBankAccountClicked', USER_NS + this.session.getUserId());
  }

  public setFleetCardApplicationNudgeExperimentMessageType(messageType: string): void {
    setValueInNamespace('fleetCardApplicationNudgeExperimentMessageType', messageType, USER_NS + this.session.getUserId());
  }

  public getFleetCardApplicationNudgeExperimentMessageType(): string {
    return getValueFromNamespace('fleetCardApplicationNudgeExperimentMessageType', USER_NS + this.session.getUserId());
  }
}